
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash, Plus, Pencil, Search, Percent, PercentCircle, Loader2 } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { BASE_URL } from '@/config/api';
import { useIsMobile } from '@/hooks/use-mobile';
import { Badge } from '@/components/ui/badge';

interface Tax {
  id: string;
  tax_name: string;
  tax_percentage: number;
  tax_number: string;
  tax_amount?: number;
}

interface TaxesManagerProps {
  small?: boolean;
}

const TaxesManager: React.FC<TaxesManagerProps> = ({ small = false }) => {
  const { token } = useAuth();
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [filteredTaxes, setFilteredTaxes] = useState<Tax[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentTax, setCurrentTax] = useState<Tax | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [taxToDelete, setTaxToDelete] = useState<Tax | null>(null);
  const isMobile = useIsMobile();

  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    tax_name: '',
    tax_percentage: '',
    tax_number: '',
    tax_amount: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTaxes, setIsLoadingTaxes] = useState(true);
  const { toast } = useToast();

  const fetchTaxes = async () => {
    setIsLoadingTaxes(true);
    try {
      const response = await fetch(`${BASE_URL}/api/taxes`, {
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTaxes(data.taxes || []);
        setFilteredTaxes(data.taxes || []);
      } else {
        toast({
          title: "Failed to fetch taxes",
          description: "There was an error loading the taxes list.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error fetching taxes:', error);
      toast({
        title: "Failed to fetch taxes",
        description: "There was an error loading the taxes list.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingTaxes(false);
    }
  };

  // Fetch taxes on component mount
  useEffect(() => {
    fetchTaxes();
  }, [token]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredTaxes(taxes);
    } else {
      const filtered = taxes.filter((tax) => 
        tax.tax_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tax.tax_number.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredTaxes(filtered);
    }
  }, [searchQuery, taxes]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const resetForm = () => {
    setFormData({
      tax_name: '',
      tax_percentage: '',
      tax_number: '',
      tax_amount: ''
    });
    setCurrentTax(null);
    setIsEditMode(false);
  };

  const openAddDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (tax: Tax) => {
    setCurrentTax(tax);
    setFormData({
      tax_name: tax.tax_name,
      tax_percentage: tax.tax_percentage.toString(),
      tax_number: tax.tax_number,
      tax_amount: tax.tax_amount?.toString() || ''
    });
    setIsEditMode(true);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
  
    if (!formData.tax_name?.trim() || !formData.tax_percentage?.trim()) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }
  
    setIsLoading(true);
  
    try {
      const endpoint = isEditMode
        ? `${BASE_URL}/api/tax/update`
        : `${BASE_URL}/api/tax/create`;
  
      const requestData = {
        ...(isEditMode && currentTax ? { id: currentTax.id } : {}),
        tax_name: formData.tax_name?.trim() || '',
        tax_percentage: parseFloat(formData.tax_percentage || '0'),
        tax_number: formData.tax_number?.trim() || '',
        ...(formData.tax_amount ? { tax_amount: parseFloat(formData.tax_amount) } : {})
      };
  
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${isEditMode ? 'update' : 'add'} tax`);
      }
  
      // Refresh the taxes list
      fetchTaxes();
  
      toast({
        title: isEditMode ? "Tax updated" : "Tax added",
        description: `${formData.tax_name} has been ${isEditMode ? 'updated' : 'added'} successfully`
      });
  
      setIsDialogOpen(false);
      resetForm();
  
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} tax:`, error);
      toast({
        title: `Failed to ${isEditMode ? 'update' : 'add'} tax`,
        description: error.message || "An error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  

  const handleDelete = async (id: string) => {
    const taxToDelete = taxes.find(tax => tax.id === id);
    
    try {
      const response = await fetch(`${BASE_URL}/api/tax/delete`, {
        method: 'POST',
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id })
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete tax');
      }
      
      // Refresh the taxes list
      fetchTaxes();
      
      toast({
        title: "Tax deleted",
        description: `${taxToDelete?.tax_name || 'The tax'} has been deleted successfully`
      });
    } catch (error) {
      console.error('Error deleting tax:', error);
      toast({
        title: "Failed to delete tax",
        description: "An error occurred while trying to delete the tax. Please try again.",
        variant: "destructive"
      });
    }
  };

  const CardComponent = small ? React.Fragment : Card;
  const extraProps = small ? {} : { className: "mb-6" };

  return (
    <CardComponent {...extraProps}>
      {!small && (
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Percent className="h-5 w-5" />
            {t('taxes.title')}
          </CardTitle>
        </CardHeader>
      )}
      
      <CardContent className={small ? "p-0" : ""}>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="relative w-48 max-w-xs">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t('taxes.search_taxes')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
            <Button 
              onClick={openAddDialog} 
              className="gap-1 ml-2"
            >
              <Plus className="h-4 w-4" />
              {isMobile ? '' : t('taxes.add')}
            </Button>
          </div>
          
          {isLoadingTaxes ? (
            <div className="text-center py-4">
              <Loader2 className="mr-2 h-4 w-4 animate-spin inline-block" />
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTaxes.map((tax) => (
                <div 
                  key={tax.id}
                  className="flex items-center justify-between p-2 rounded-md bg-secondary/50"
                >
                  <div>
                    <div className="font-medium">
                      <span className='mr-1'>
                        {tax.tax_name}
                      </span> 
                      {tax.tax_number && (
                       <Badge
                        variant="outline">
                          {tax.tax_number}
                      </Badge>
                      )}
                      </div>
                    <div className="text-sm text-muted-foreground">
                      {tax.tax_percentage}%
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(tax)}
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setTaxToDelete(tax);
                        setIsDeleteDialogOpen(true);
                      }}
                      className="h-8 w-8 text-destructive hover:text-destructive"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              {filteredTaxes.length === 0 && !isLoadingTaxes && (
                <div className="text-center p-4 text-muted-foreground">
                  {searchQuery ? "No matching taxes found" : "No taxes added yet"}
                </div>
              )}
            </div>
          )}
        </div>

        


        <AlertDialog  open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('taxes.confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('taxes.confirmDeleteTaxe', { name: taxToDelete?.tax_name }) 
                  || `Are you sure you want to delete "${taxToDelete?.tax_name}"? This action cannot be undone.`}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className='mt-5'>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                  if (taxToDelete) {
                    handleDelete(taxToDelete.id);
                    setIsDeleteDialogOpen(false);
                    setTaxToDelete(null);
                  }
                }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className={isMobile ? "w-full h-[100dvh] max-h-[100dvh] flex flex-col" : "max-w-[600px] max-h-[80vh] flex flex-col"}>
            <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-5"}>
              <DialogTitle className="flex items-center gap-2">
                <PercentCircle className="h-5 w-5 text-primary" />
                {isEditMode ? "Edit Tax" : "Add New Tax"}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className={`${!isMobile ? 'h-auto' : 'h-full'} flex flex-col`}>
              <div className={`space-y-4 flex-grow overflow-auto ${!isMobile ? 'max-h-[60vh] pb-5' : 'p-5'}`}>
                <div className="space-y-2">
                  <Label htmlFor="tax_name">{t('taxes.tax_name')}</Label>
                  <Input
                    id="tax_name"
                    name="tax_name"
                    value={formData.tax_name}
                    onChange={handleInputChange}
                    placeholder="e.g., VAT, GST"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="tax_percentage">{t('taxes.percentage')} (%)</Label>
                  <Input
                    id="tax_percentage"
                    name="tax_percentage"
                    type="number"
                    step="0.01"
                    value={formData.tax_percentage}
                    onChange={handleInputChange}
                    placeholder="e.g., 20"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="tax_number">{t('taxes.tax_number')}</Label>
                  <Input
                    id="tax_number"
                    name="tax_number"
                    value={formData.tax_number}
                    onChange={handleInputChange}
                    placeholder="e.g., TX12345"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="tax_amount">{t('taxes.amount')} ({t('taxes.optional')})</Label>
                  <Input
                    id="tax_amount"
                    name="tax_amount"
                    type="number"
                    step="0.01"
                    value={formData.tax_amount}
                    onChange={handleInputChange}
                    placeholder="e.g., 100"
                  />
                </div>
              </div>
              
              <DialogFooter className={isMobile ? "px-6 py-4 border-t sticky bottom-0 bg-gray-50 z-20" : "pt-5"}>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? t('common.processing') : isEditMode ? t('taxes.update') : t('taxes.add')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </CardComponent>
  );
};

export default TaxesManager;
